import { Button } from '@astryxdesign/core/Button';
import { Heading } from '@astryxdesign/core/Heading';
import { List, ListItem } from '@astryxdesign/core/List';
import { Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/VStack';
import { CalendarBlank, CaretRight } from '@phosphor-icons/react';
import { useSyncExternalStore } from 'react';
import { EmptyView } from '@/components/StateViews';
import { useBookingFlow } from '@/features/booking/useBookingFlow';
import { useStudioData } from '@/features/studio/StudioContext';
import { fmtDateTime } from '@/lib/format';
import { listBookings } from '@/lib/myBookings';
import { studioUrl } from '@shared/routes';

function useStoredBookings(slug: string) {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener('my-bookings-changed', cb);
      window.addEventListener('storage', cb);
      return () => {
        window.removeEventListener('my-bookings-changed', cb);
        window.removeEventListener('storage', cb);
      };
    },
    () => JSON.stringify(listBookings(slug)),
  );
}

export function MyBookingsPage() {
  const studio = useStudioData();
  const flow = useBookingFlow();
  const items = JSON.parse(useStoredBookings(studio.slug)) as ReturnType<typeof listBookings>;
  const now = new Date().toISOString();
  const upcoming = items.filter((b) => b.startsAt >= now).reverse();
  const past = items.filter((b) => b.startsAt < now);

  return (
    <VStack gap={6} paddingInline={4} paddingBlock={6} className="page-column">
      <Heading level={1}>Моя запись</Heading>
      {items.length === 0 ? (
        <EmptyView
          icon={<CalendarBlank size={32} />}
          title="Записей пока нет"
          description="Запишитесь — ссылка на запись сохранится на этом устройстве."
          action={<Button label="Записаться" variant="primary" onClick={() => flow.open()} />}
        />
      ) : (
        <>
          {upcoming.length > 0 && (
            <List hasDividers density="spacious" header={<Text type="label">Предстоящие</Text>}>
              {upcoming.map((b) => (
                <ListItem
                  key={b.id}
                  label={b.serviceName}
                  description={`${fmtDateTime(b.startsAt, studio.timezone)} · код ${b.code}`}
                  endContent={<CaretRight size={18} aria-hidden />}
                  href={studioUrl.booking(studio.slug, b.id)}
                />
              ))}
            </List>
          )}
          {past.length > 0 && (
            <List hasDividers header={<Text type="label">Прошедшие</Text>}>
              {past.map((b) => (
                <ListItem
                  key={b.id}
                  label={b.serviceName}
                  description={fmtDateTime(b.startsAt, studio.timezone)}
                  href={studioUrl.booking(studio.slug, b.id)}
                />
              ))}
            </List>
          )}
          <Button label="Новая запись" onClick={() => flow.open()} />
        </>
      )}
    </VStack>
  );
}
